import React, { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import JanesChatbot from '../components/JanesChatbot'
import Sidebar from '../components/Sidebar'
import Header from '../components/Header'
import { AIChatFAB } from '@/components/ai/AIChatFAB'
import { Z_INDEX } from '@/lib/constants/z-index'
import { useEvaluationUIStore } from '@/stores/evaluation-ui-store'

interface AuthLayoutProps {
  children: React.ReactNode
}

/**
 * Authenticated App Layout
 *
 * Provides full application chrome for authenticated users:
 * - Header (top bar) with navigation controls and user menu
 * - Sidebar (desktop) with application navigation
 * - Mobile sidebar drawer that slides in from left
 * - Main content area with responsive padding
 * - Janes chatbot integration
 *
 * This layout is used for all authenticated routes (/, /dashboard, /employees, etc.)
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [janesChatbotOpen, setJanesChatbotOpen] = useState(false)
  const focusMode = useEvaluationUIStore((s) => s.focusMode)

  const handleLogout = async () => {
    try {
      await logout()
      // Redirect is now handled in the auth context
    } catch (error) {
      console.error('Error during logout:', error)
      // Still attempt to redirect even if there's an error
      window.location.href = '/login'
    }
  }

  return (
    <div className={`min-h-screen bg-brand-dark-light relative ${focusMode ? '' : 'flex overflow-hidden'}`}>
      {/* Sidebar - Hidden in focus mode */}
      {!focusMode && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={user}
          onJanesChatbotOpen={() => setJanesChatbotOpen(true)}
        />
      )}

      {/* Content Area - Floating panel with rounded corners */}
      <div className={`flex-1 min-w-0 overflow-x-hidden relative ${focusMode ? '' : 'z-10 lg:my-4 lg:mr-4 lg:rounded-2xl bg-brand-dark-deepest overflow-hidden'}`}>
        {/* Header - Hidden in focus mode */}
        {!focusMode && (
          <Header
            onMobileMenuOpen={() => setSidebarOpen(true)}
            user={user}
            onLogout={handleLogout}
          />
        )}

        {/* Page Content */}
        <main className={focusMode ? '' : 'flex-1 p-4 sm:p-6 min-w-0 overflow-x-hidden'}>
          <div className={focusMode ? '' : 'animate-fade-in-up'}>
            {children}
          </div>
        </main>
      </div>

      {/* Sidebar Overlay - Enhanced with backdrop blur */}
      {sidebarOpen && !focusMode && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm lg:hidden animate-fade-in"
          style={{ zIndex: Z_INDEX.SIDEBAR_BACKDROP }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Janes Chatbot */}
      {!focusMode && (
        <JanesChatbot
          isOpen={janesChatbotOpen}
          onClose={() => setJanesChatbotOpen(false)}
        />
      )}

      {/* AI Chat FAB — visible only on routes with a configured persona */}
      {!focusMode && <AIChatFAB />}
    </div>
  )
}
